import CryptoJS from 'crypto-js'
import { ElMessageBox as MessageBox } from 'element-plus'
import { h } from 'vue'
import type { AxiosResponse } from 'axios'
import CopyBox from '@/components/copy-box/index.vue'

// const elementResizeDetectorMaker = require('element-resize-detector')
// export const watchSize = (id) => {
//   const erd = elementResizeDetectorMaker()
//   erd.listenTo(document.getElementById(id), (element) => {
//     return [element.offsetWidth, element.offsetHeight]
//   })
// }

/**
 * 防抖函数 - 在事件被触发 delay 时间后执行一次函数调用，如果 delay 时间内事件被多次触发，则重新计时
 * @param {Function} callback - 回调函数
 * @param {Number} delay - 延迟时间
 */
export function debounce(callback, delay = 0) {
  let timer = null

  return (...args) => {
    clearTimeout(timer)

    timer = setTimeout(() => {
      callback.apply(this, args)
    }, delay)
  }
}

// 获取数字格式的月份
export function toNumber(number: number) {
  if (number < 10)
    return `0${number}`

  return number
}

// 密码验证，要求：
// 1). 8-16位
// 2). 英文大、小写字母、数字和特殊字符中至少 3 种类型的字符
export const passwordReg
  = /^(?:(?=.*[a-z])(?=.*\d)(?=.*[@!%*#_?&])|(?=.*[a-z])(?=.*\d)(?=.*[A-Z])|(?=.*\d)(?=.*[A-Z])(?=.*[@!%*#_?&])|(?=.*[a-z])(?=.*[A-Z])(?=.*[@!%*#_?&]))[A-Za-z\d@!%*#_?&]{8,16}$/
export const validatePassword = (rule, value, callback) => {
  if (!value) {
    callback(new Error('请输入密码'))
  } else {
    const reg = passwordReg
    if (!reg.test(value))
      callback(new Error('包含英文大小写、数字和特殊字符的至少3种类型(8-16位)'))
    else
      callback()
  }
}

// 判断值是否为空
export const validateEmpty = (message) => {
  return (rule, value, callback) => {
    if (!value)
      callback(new Error(message))
    else
      callback()
  }
}

export const userNameReg = /(?=.*[A-Za-z])^[A-Za-z0-9][a-zA-Z0-9_-]{3,19}$/
export const validateUserName = (rule, value, callback) => {
  if (!value) {
    callback(new Error('请输入用户名'))
  } else {
    if (userNameReg.test(value))
      callback()
    else
      callback(new Error('支持字母、数字、- _；包含字母且不以- _开头(4-20位)'))
  }
}

// 手机号验证
export const phoneReg = /^1[2-9]\d{9}$/

// 账户密码错误
export const validateUserNameOrPhone = (rule, value, callback) => {
  if (!value) {
    callback(new Error('请输入用户名/手机号'))
  } else {
    if (userNameReg.test(value) || phoneReg.test(value))
      callback()
    else
      callback(new Error('账户格式错误'))
  }
}

export const validatePhoneNumber = (rule, value, callback) => {
  if (!value) {
    callback(new Error('请输入手机号'))
  } else {
    const reg = phoneReg
    if (!reg.test(value))
      callback(new Error('手机号格式有误'))
    else
      callback()
  }
}
export const generateRules = () => {
  return {
    username: [{ validator: validateUserName, trigger: 'blur' }],
    phone: [{ validator: validatePhoneNumber, trigger: 'blur' }],
    smsCode: [
      { validator: validateEmpty('请输入手机验证码'), trigger: 'blur' },
    ],
    captcha: [
      { validator: validateEmpty('请输入图形验证码'), trigger: 'blur' },
      { len: 4, message: '长度不正确', trigger: ['blur'] },
    ],
    // 旧密码
    oldPassword: [{ validator: validatePassword, trigger: ['blur', 'change'] }],
    // 新密码
    newPassword: [{ validator: validatePassword, trigger: ['blur', 'change'] }],
    // 登录密码
    password: [{ validator: validatePassword, trigger: ['blur', 'change'] }],
    // 初始秘密
    initialPassword: [
      { validator: validatePassword, trigger: ['blur', 'change'] },
    ],
  }
}

// 生成AES密钥
export function generateKey(keySource: string) {
  const str = keySource.slice(0, 16).padEnd(16, '0')
  console.log('str', str)
  return CryptoJS.enc.Utf8.parse(str)
}

// AES加密
export function encrypt(source: string, keySource: string) {
  const key = generateKey(keySource) // 16位
  const encrypted = CryptoJS.AES.encrypt(source, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  })
  console.log('encrypted.toString()', encrypted.toString())
  console.log(
    'encrypted.ciphertext.toString()',
    encrypted.ciphertext.toString(),
  )
  return encrypted.ciphertext.toString()
}

// 解密AES密码
export function decrypt(source: string, keySource: string) {
  const key = generateKey(keySource) // 16位
  const hex = CryptoJS.enc.Hex.parse(source) // 转成16进制
  const base64 = CryptoJS.enc.Base64.stringify(hex) // base64
  const bytes = CryptoJS.AES.decrypt(base64, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  })
  return CryptoJS.enc.Utf8.stringify(bytes)
}

// 下载文件 并且重新取名字
export function downloadFile(url: string, newFilename: string) {
  const xhr = new XMLHttpRequest()
  xhr.open('GET', url, true)
  xhr.responseType = 'blob'

  xhr.onload = () => {
    if (xhr.status === 200) {
      const blob = xhr.response
      const downloadUrl = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = newFilename
      link.click()

      // 清理临时 URL
      URL.revokeObjectURL(downloadUrl)
    }
  }

  xhr.send()
}

// 复制用户名密码
export function copyPassword(userName: string, password: string) {
  const passwordText = decrypt(password, userName)
  MessageBox({
    title: '账户密码',
    message: () =>
      h('div', [
        h('p', { class: 'content' }, '请及时复制账号密码，关闭弹窗后密码不可见。'),
        h(CopyBox, {
          list: [{
            label: '用户名：',
            value: userName,
          }, {
            label: '密码：',
            value: passwordText,
          }],
        }),
      ]),
    showConfirmButton: false,
    showCancelButton: true,
    cancelButtonText: '关闭',
    customClass: 'message-box__credentials',
    type: 'warning',
  })
}

export const basicParams = {
  client_id: 'client_super_admin',
  client_secret: '123456',
  scope: 'app',
  serviceType: SERVICE_TYPE,
}
// 生成登录参数
export function generateParams(params) {
  return {
    ...basicParams,
    ...params,
  }
}

// 登录
export const SERVICE_SUB_TYPE_LOGIN = `${SERVICE_TYPE}-login`
// 忘记密码
export const SERVICE_SUB_TYPE_FORGET_PASSWORD = `${SERVICE_TYPE}-forget-password`
// 修改手机号码
export const SERVICE_SUB_TYPE_BIND = `${SERVICE_TYPE}-bind`
// 更换密码前的-身份校验
export const SERVICE_SUB_TYPE_PASS_WORD = `${SERVICE_TYPE}-change-password`

export function isNullOrUndefined(value: any) {
  return value === null || value === undefined
}

export function nullFormat(value: any) {
  return isNullOrUndefined(value) ? '-' : Number(value).toKeep(2)
}
export function nullFormatStr(value: any) {
  return isNullOrUndefined(value) ? '-' : value
}

export function nullFormatString(value: any) {
  return isNullOrUndefined(value) ? '-' : value
}

export function isNullOrEmpty(value:any) {
  return !value || !value.length
}

// 获取静态资源图片的路径
export function getImage(name: string): string {
  return new URL(`/src/assets/imgs/${name}`, import.meta.url).href
}

export function download(url: string, fileName?: string) {
  const $el: HTMLAnchorElement = document.querySelector('#J_download-link') || document.createElement('a')
  $el.setAttribute('id', 'J_download-link')
  $el.setAttribute('style', 'visibility:hidden;height:0;width:0;')
  $el.setAttribute('target', '_self')
  $el.setAttribute('href', url)
  fileName && $el.setAttribute('download', fileName || '')
  document.body.appendChild($el)
  $el.click()
  document.body.removeChild($el)
}

// 将搜索框中el-date-picker type为daterange的日期段Fri Nov 10 2023 00:00:00 GMT +0800(中国标准时间) 格式化成 "yyyy-MM-dd" 格式
export function formatDay(date: any) {
  const originalDate1 = new Date(date)

  // 获取年、月、日
  const year1 = originalDate1.getFullYear()
  const month1 = originalDate1.getMonth() + 1 // 月份是从 0 开始的，需要加 1
  const day1 = originalDate1.getDate()

  const formattedDate = `${year1}-${month1 < 10 ? '0' : ''}${month1}-${day1 < 10 ? '0' : ''}${day1}`

  return formattedDate
}

export const personRules = () => {
  return {
    phoneNumber: [{ validator: validatePhoneNumber, trigger: 'blur', required: true }],
    smsCode: [
      { validator: validateEmpty('请输入四位数字验证码'), trigger: 'blur', required: true },
    ],
    // 旧密码
    oldPassword: [{ validator: validatePassword, trigger: ['blur', 'change'] }],
    // 新密码
    newPassword: [{ validator: validatePassword, trigger: ['blur', 'change'] }],
  }
}

export function downloadBlob(response: AxiosResponse, name?: string) {
  const { headers, data } = response
  const disposition = headers['content-disposition']
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
  let fileName
  if (name) // 传name就取name
    fileName = name
  else if (disposition) // 没传name就取响应标头中content-disposition中的文件名filename
    fileName = decodeURIComponent(filenameRegex.exec(disposition)![1])
  else// 最后如果都没有，就取file
    fileName = 'file'

  const blob = new Blob([data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const href = window.URL.createObjectURL(blob)
  console.log('downloadBlob', fileName, response)
  download(href, fileName)
}
